import React, { useState } from 'react';
import classNames from 'classnames';
// @ts-ignore // Can't find no working type for external lib
import Swiper from 'swiper';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useDispatch, useSelector } from 'react-redux';
import ProductSwiperItem from './ProductSwiperItem';
import { translate } from '../../services/L10n';
import constants from '../../settings/constants';
import { DROP_ITEM, GRAB_ITEM } from '../../settings/events';
import styles from './ProductSwiper.module.less';
import { Icon } from '../Icon';
import swiperSettings from './SwiperSettings';
import { preventDefault } from '../../util/events';
import { TOUCH_SUPPORT } from '../../util/supportedEvents';
import emitter from '../../emitter';
import {
  POINTER_LEAVE,
  POINTER_UP,
  POINTER_DOWN,
} from '../../util/supportedEvents';
import localStatisticsActions from '../../services/statistics/insights/custom/local/localStatisticsActions';
import swiperService from '../../services/swiper';
import productService from '../../services/products';
import MountingRailSwitch from './MountingRailSwitch';
import { getLocalEvent } from '../../services/statistics/insights/custom/local/localStatisticsEvents';
import {
  selectHasScenePreloadFinished,
  selectIsWallResizerActive,
} from '../../state/scene/sceneSelectors';
import {
  selectCurrentColorFilter,
  selectProductMenuFilter,
  selectFilteredSwiperItems,
  selectCurrentSubFilterValue,
} from '../../state/productMenu/productMenuSelectors';
import { selectDisableMountingRails } from '../../state/tac/tacSelectors';
import {
  selectIsLandscape,
  selectIsMobile,
  selectIsPortrait,
  selectIsTablet,
  selectIsWidescreen,
  selectUserAgent,
} from '../../state/userAgent/userAgentSelectors';
import { ProductMenuItem } from '../../state/productMenu/productMenuTypes';
import { thunkSetUsingMountingRail } from '../../state/tac/tacThunks';
import { FilterObject } from '../../services/products/productsServiceTypes';
import {
  KompisIconPill,
  KompisText,
  KompisPopover,
  KompisPopoverPosition,
  KompisPopoverPadding,
} from '@inter-ikea-kompis/react-components';
import {
  PopoverAlignmentEnum,
  PopoverDirectionEnum,
} from '@inter-ikea-kompis/component-popover';
import { InformationIcon } from '@inter-ikea-kompis/icons';
import { IconPillSizeOptionsEnum } from '@inter-ikea-kompis/component-icon-pill';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { t } from '../../translations';

const ProductSwiper = ({ forwardEvent }: any) => {
  const wallResizerActive = useSelector(selectIsWallResizerActive);
  const disableMountingRails = useSelector(selectDisableMountingRails);
  const currentFilter = useSelector(selectProductMenuFilter);
  const isPortrait = useSelector(selectIsPortrait);
  const currentColorFilter = useSelector(selectCurrentColorFilter);
  const userAgent = useSelector(selectUserAgent);
  const isMobile = useSelector(selectIsMobile);
  const isTablet = useSelector(selectIsTablet);
  const isLandscape = useSelector(selectIsLandscape);
  const scenePreloadDone = useSelector(selectHasScenePreloadFinished);
  const isWideScreen = useSelector(selectIsWidescreen);
  const filterTextKey = useSelector(swiperService.getSwiperFilterText);
  const items = useSelector(selectFilteredSwiperItems).map(([item]) => item);
  const currentSubFilter = useSelector(selectCurrentSubFilterValue);

  const [infoButtonSelected, setInfoButtonSelected] = useState(false);

  const onSelect = () => setInfoButtonSelected(!infoButtonSelected);

  const onClickAnywhere = (e: any) =>
    e.target.parentNode?.id !== 'info-pill-wrapper' &&
    setInfoButtonSelected(false);

  const dispatch = useDispatch();
  const setUseMountingRails = (disableMountingRails: boolean) =>
    dispatch(thunkSetUsingMountingRail(disableMountingRails));

  const rootRef = React.useRef<HTMLDivElement>(null);
  const swiperRef = React.useRef<HTMLDivElement>(null);
  const swiper = React.useRef<Swiper>(null);
  const unsubscribe = React.useRef<(() => any)[]>();

  const { labelTranslationKey, name: filterName } = currentFilter;

  /**
   * Get dimension values
   * @param items
   * @param dimensionKey
   */
  const getDimensionValues = (items: ProductMenuItem[], dimensionKey: any) =>
    items
      // @ts-ignore LIMITATION OF ANCIENT, PRE-HISTORIC TS-VERSION NOT SUPPORTING ENUMS
      .map((item: ProductMenuItem) => item.product[dimensionKey])
      .filter((item, pos, arr) => arr.indexOf(item) === pos)
      .sort();

  /**
   * Is valid product
   * @param item
   */
  const isValidProduct = ({ product: { id } }: ProductMenuItem) =>
    id !== 'missing';

  /**
   * Create item pairs
   * @param items
   * @param itemPairs
   */
  const createItemPairs = (
    items: ProductMenuItem[],
    itemPairs: ProductMenuItem[][]
  ): ProductMenuItem[][] => {
    if (items.length < 2)
      return items.length ? [...itemPairs, items] : itemPairs;

    const [item1, item2, ...rest] = items;
    return createItemPairs(rest, [...itemPairs, [item1, item2]]);
  };

  /**
   * Get paired product slides
   * @param allItems
   */
  const getPairedProductSlides = (allItems: ProductMenuItem[]) => {
    if (!currentFilter.pairingSettings) return;

    const pairKey = currentFilter.pairingSettings.key;
    const validItems = allItems.filter(isValidProduct);
    const pairingDimensionValues = getDimensionValues(allItems, pairKey);

    if (pairingDimensionValues.length !== 2) {
      return validItems.map(item => makeSlide([item]));
    } else {
      if (isWideScreen) {
        // @ts-ignore LIMITATION OF ANCIENT, PRE-HISTORIC TS-VERSION NOT SUPPORTING ENUMS
        return createItemPairs(validItems, []).map(items => makeSlide(items));
      } else {
        return pairingDimensionValues.map(value =>
          allItems
            // @ts-ignore LIMITATION OF ANCIENT, PRE-HISTORIC TS-VERSION NOT SUPPORTING ENUMS
            .filter(({ product }) => product[pairKey] === value)
            .map(product => makeSlide([product]))
        );
      }
    }
  };

  /**
   * Should render multiline
   * @param allItems
   */
  const shouldRenderMultiline = (allItems: ProductMenuItem[]) =>
    !isVertical() &&
    allItems.some(
      item =>
        // @ts-ignore we need to find a different way of handling these range specific returns instead of
        // errors, since it really upsets poor TS
        (!swiperService.shouldDisplayProductType(item.product) &&
          swiperService.displayColor(
            item.product,
            currentFilter,
            currentColorFilter
          )) ||
        swiperService.shouldDisplayProductType(item.product)
    );

  /**
   * Get product slides
   */
  const getProductSlides = () => {
    if (!scenePreloadDone || !items?.length) return null;

    const renderMultiline = shouldRenderMultiline(items);

    if (currentFilter.pairingSettings) return getPairedProductSlides(items);

    return items.map((items: any) => makeSlide([items], renderMultiline));
  };

  /**
   * Disable swiping
   */
  const _disableSwiping = () => (swiper.current.allowTouchMove = false);

  /**
   * Enable swiping
   */
  const _enableSwiping = () => (swiper.current.allowTouchMove = true);

  /**
   * Prevent default event
   * @param event
   */
  const _preventDefaultEvent = (event: any) =>
    rootRef.current &&
    rootRef.current.addEventListener(event, preventDefault, { passive: false });

  /**
   * Is vertical
   */
  const isVertical = () => (!isMobile && !isTablet) || !isPortrait;

  React.useEffect(() => {
    if (TOUCH_SUPPORT) {
      _preventDefaultEvent('touchmove');
    }

    _preventDefaultEvent('wheel');
    _preventDefaultEvent('scroll');

    addSwiper();

    emitter.on(GRAB_ITEM, _disableSwiping);
    emitter.on(DROP_ITEM, _enableSwiping);

    // NIF markets can't handle window.parent. Catch possible error.
    try {
      if (window.parent !== window) {
        unsubscribe.current = [handleMouseOutsideIframe()];
      }
    } catch (error) {}

    return () => {
      unsubscribe.current &&
        unsubscribe.current.forEach((fn: () => any) => fn());

      emitter.off(GRAB_ITEM, _disableSwiping);
      emitter.off(DROP_ITEM, _enableSwiping);

      killSwiper();
    };
  }, []);

  React.useEffect(() => {
    addSwiper();
  }, [currentFilter, currentSubFilter]);

  React.useEffect(() => {
    window.addEventListener(POINTER_DOWN, onClickAnywhere);
    return () => window.removeEventListener(POINTER_DOWN, onClickAnywhere);
  }, []);

  /**
   * Handle mouse outside iframe event
   */
  const handleMouseOutsideIframe = () => {
    // NIF markets can't handle window.parent. Catch possible error.
    try {
      window.parent.addEventListener(POINTER_UP, stopSwiping);

      return () => window.parent.removeEventListener(POINTER_UP, stopSwiping);
    } catch (e) {
      document.addEventListener(POINTER_LEAVE, stopSwiping);

      return () => document.removeEventListener(POINTER_LEAVE, stopSwiping);
    }
  };

  /**
   * Stop swiping
   */
  const stopSwiping = () =>
    swiper.current && swiper.current.$el.trigger(POINTER_UP);

  /**
   * Kill swiper
   */
  const killSwiper = () => swiper.current && swiper.current.destroy();

  /**
   * Add swiper
   */
  const addSwiper = () => {
    const wasMouseEntered = swiper.current?.mouseEntered;
    killSwiper();
    swiper.current = new Swiper(
      `.${styles.swiperContainer}`,
      swiperSettings((!isMobile && !isTablet) || !isPortrait)
    );
    swiper.current.mouseEntered = wasMouseEntered;
  };

  /**
   * Report swiper analytics
   */
  const reportSwiperAnalytics = () =>
    getLocalEvent(
      localStatisticsActions.PRODUCT_MENU_SWIPER_INTERACTION
    ).reportEvent({ interaction: 'arrow' });

  /**
   * Render product swiper item
   * @param items
   * @param renderMultiline
   */
  const renderProductSwiperItems = (
    items: ProductMenuItem[],
    renderMultiline: boolean
  ) => {
    // @ts-ignore LIMITATION OF ANCIENT, PRE-HISTORIC TS-VERSION NOT SUPPORTING ENUMS
    const mode = constants.DRAG_MODE[currentFilter.dragMode];

    return items.map(item => (
      <ProductSwiperItem
        key={item.product.id}
        product={item.product}
        disabled={item.disabled}
        mode={mode}
        forwardEvent={forwardEvent}
        isVertical={isVertical()}
        userAgent={userAgent}
        renderMultiline={renderMultiline}
      />
    ));
  };

  /**
   * Renders filters
   */
  const renderFilters = () => {
    const filters = productService
      .getFilters()
      .map(({ Component }: FilterObject, index: number) => (
        <Component key={index} />
      ));

    return filters.length ? (
      <div className={classNames(styles.swiperSlide, styles.filterWrapper)}>
        {filters}
      </div>
    ) : null;
  };

  /**
   * Get header
   */
  const getHeader = () =>
    !isPortrait && (
      <div
        className={classNames(styles.swiperSlide, styles.header)}
        key={labelTranslationKey}
      >
        <div
          key={labelTranslationKey}
          className={classNames(styles.headerLabel)}
          data-testid="swiper-header-label"
        >
          {translate(t[labelTranslationKey])}
        </div>
      </div>
    );

  /**
   * Make slide
   */
  const makeSlide = (items: ProductMenuItem[], renderMultiline = false) => (
    <div
      key={`${items[0].product.id}-${(items[1] || items[0]).product.id}`}
      data-testid="product-swiper-item"
      className={classNames(
        styles.swiperSlide,
        styles[currentFilter.swiperLayout.name]
      )}
    >
      {renderProductSwiperItems(items, renderMultiline)}
    </div>
  );

  /**
   * Render mounting rail switch
   */
  const renderMountingRailSwitch = () =>
    currentFilter.settings?.mountingRail && (
      <MountingRailSwitch
        checked={!disableMountingRails}
        onChange={setUseMountingRails}
        isMobile={isMobile}
        isLandscape={isLandscape}
      />
    );

  /**
   * Get settings slide
   */
  const getSettingsSlide = () =>
    currentFilter.settings && (
      <div
        className={classNames(styles.swiperSlide, styles.settings)}
        key={'settings'}
      >
        {renderMountingRailSwitch()}
      </div>
    );

  const renderConfigurators = () => {
    const configs = swiperService
      .getSwiperConfigSectionItems()
      .reduce<React.FunctionComponent[]>(
        // @ts-ignore
        (acc, { condition, appliesToFilter, Component }, index) => {
          return condition() && appliesToFilter === filterName
            ? [...acc, <Component key={index} />]
            : acc;
        },
        []
      );

    return configs ? <div className={styles.swiperSlide}>{configs}</div> : null;
  };

  /**
   * Render filter text component for smaller screens
   */
  const renderMobileFilterTextComponent = () => (
    <div className={classNames(styles.swiperSlide, styles.swiperFilterText)}>
      <KompisPopoverPosition>
        <div id="info-pill-wrapper">
          <KompisIconPill
            icon={InformationIcon}
            theme={SkapaTheme}
            iconPillSize={IconPillSizeOptionsEnum.small}
            selected={infoButtonSelected}
            onSelect={onSelect}
          />
        </div>
        <KompisPopover
          theme={SkapaTheme}
          alignment={
            isLandscape
              ? PopoverAlignmentEnum.center
              : PopoverAlignmentEnum.start
          }
          direction={
            isLandscape
              ? PopoverDirectionEnum.bottom
              : PopoverDirectionEnum.leading
          }
          visible={infoButtonSelected}
        >
          <KompisPopoverPadding theme={SkapaTheme}>
            <KompisText
              type={ThemeFontStyleTypeEnum.bodySmall}
              theme={SkapaTheme}
            >
              {translate(t[filterTextKey!])}
            </KompisText>
          </KompisPopoverPadding>
        </KompisPopover>
      </KompisPopoverPosition>
    </div>
  );

  /**
   * Render filter text component for larger screens
   */
  const renderFilterTextComponent = () => (
    <div className={classNames(styles.swiperSlide, styles.swiperFilterText)}>
      <KompisText theme={SkapaTheme}>{translate(t[filterTextKey!])}</KompisText>
    </div>
  );

  /**
   * Render text for filter
   */
  const renderSwiperFilterText = () => {
    if (!filterTextKey) return null;

    return isMobile || isTablet
      ? renderMobileFilterTextComponent()
      : renderFilterTextComponent();
  };

  return (
    <React.Fragment>
      <div
        ref={rootRef}
        className={classNames(
          styles.ProductSwiper,
          wallResizerActive && isMobile && styles.hidden
        )}
      >
        <div
          className={classNames(styles.swiperButton, styles.swiperButtonPrev)}
          onClick={reportSwiperAnalytics}
        >
          <div className={styles.icon}>
            <Icon iconName="ChevronUpIcon" />
          </div>
        </div>
        <div className={classNames(styles.swiperContainer)}>
          <div
            data-testid="product-swiper-items"
            ref={swiperRef}
            className={classNames(
              styles[currentFilter.name],
              styles.slidesWrapper
            )}
          >
            {getHeader()}
            {renderSwiperFilterText()}
            {renderConfigurators()}
            {renderFilters()}
            {getProductSlides()}
            {getSettingsSlide()}
          </div>
        </div>

        <div
          className={classNames(styles.swiperButton, styles.swiperButtonNext)}
          onClick={reportSwiperAnalytics}
        >
          <div className={styles.icon}>
            <Icon iconName="ChevronDownIcon" />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductSwiper;
